import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { useAuth } from "../context/AuthContext";
import AiUsageMeter from "./AiUsageMeter";
import "./footer.css";

/**
 * Resolves the date string of the most recent completed weekly checkpoint.
 * In GEO Platform, data processing runs weekly every Sunday.
 * If today is Sunday, the latest completed run is the previous Sunday (7 days ago).
 * Otherwise, it is the most recent past Sunday.
 */
export const getLatestCheckpointDate = (referenceDate = dayjs(), lang = "en") => {
  const d = dayjs(referenceDate);
  const dayOfWeek = d.day(); // 0 is Sunday
  const daysToSubtract = dayOfWeek === 0 ? 7 : dayOfWeek;
  const targetDate = d.subtract(daysToSubtract, "day");
  if (lang && lang.toLowerCase().startsWith("ja")) {
    return targetDate.format("YYYY年MM月DD日");
  }
  return targetDate.format("DD MMM YYYY");
};

const Footer = ({ isPermanentlyExpanded, checkpointDate }) => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const [isOnline, setIsOnline] = useState(
    typeof navigator !== "undefined" ? navigator.onLine : true
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  const client_id = user?.client_id;
  const projects = user?.projects || [];
  const currentProject = projects.find((p) => p.id === client_id);
  const brandName = currentProject?.name || client_id || "elelem GEO";
  const activeLang = i18n?.resolvedLanguage || i18n?.language || "en";
  const formattedCheckpoint = checkpointDate || getLatestCheckpointDate(undefined, activeLang);

  return (
    <footer
      className={`app-footer ${isPermanentlyExpanded ? "sidebar-expanded" : ""}`}
      role="contentinfo"
      data-testid="app-footer"
    >
      <div className="container-fluid d-flex align-items-center justify-content-between p-0 h-100">
        {/* Left Section: System status & project name */}
        <div className="footer-left-section d-flex align-items-center gap-2">
          {/* Status Dot */}
          <div
            className={`footer-status-pill ${isOnline ? "online" : "offline"}`}
            title={
              isOnline
                ? t("footer.allSystemsOperational", "All systems operational")
                : t("footer.offlineStatus", "Offline - check network connection")
            }
            data-testid="footer-status-pill"
          >
            <span className={`footer-status-dot ${isOnline ? "pulse" : "offline"}`} />
            <span className="footer-status-text">
              {isOnline
                ? t("footer.operational", "Operational")
                : t("footer.offline", "Offline")}
            </span>
          </div>

          <span className="footer-divider">•</span>

          {/* Active Workspace / Client */}
          <span className="footer-workspace-name" title={brandName}>
            {brandName}
          </span>
        </div>

        {/* Center Section: Data Freshness / Help shortcut */}
        <div className="footer-center-section d-none d-xl-flex align-items-center gap-2 text-muted">
          <span className="footer-data-checkpoint" data-testid="footer-data-checkpoint">
            {t("footer.dataFreshness", "Latest Checkpoint: {{date}}", {
              date: formattedCheckpoint,
              defaultValue: `Latest Checkpoint: ${formattedCheckpoint}`,
            })}
          </span>
        </div>

        {/* Right Section: AI Usage Meter */}
        <div className="footer-right-section d-flex align-items-center gap-2">
          <AiUsageMeter />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
